import Immutable, { Immutable as ImmutableGenericType } from 'seamless-immutable'
import get from 'lodash/get'
import { Address } from 'app-types'

import {
  SecondDocumentType,
  IdentityStatus,
  PepOptions,
  SmsVerifyResponse,
} from '../../../constants'
import { CacheSmsSendResponse } from '../../../types'

export interface ClientState {
  cacheSmsSendResponse?: CacheSmsSendResponse
  cacheVerifySmsStatus?: SmsVerifyResponse
  citizenship: string
  consentCommUnsecured: boolean
  consentInstantMessaging: boolean
  consentToOfferMarketing: boolean
  contactAddress: Address
  countryOfBirth: string
  differentContactAddress: boolean
  education: string | null
  email: string
  employedSince?: number // UNIX timestamp in ms
  employedTo?: number // UNIX timestamp in ms
  employerAddress: Address
  employerId: string // ICO
  employerName: string
  employmentTypeCode: string // Code/key to codetable,
  housingExpenditure: number | null
  firstName: string
  identityCardExpiration?: number // UNIX timestamp in ms
  identityCardNumber: string
  lastName: string
  livingType: string | null
  maritalStatus: string
  minedIdentityStatus?: IdentityStatus
  net3MonthHouseholdIncome: number | null
  net3MonthIncome: number | null
  noticePeriod: boolean // Employment notice
  numberOfDependents: number | null
  otherCostOfLiving: number | null
  otherDocumentType: SecondDocumentType | null
  otherMonthlyInstallments: number | null
  pep?: string
  pepReason?: string
  permanentAddress: Address
  personalIdentificationNumber: string
  phoneNumber: string
  placeOfBirth: string
  professionCode: string // Code/key to codetable,
  stayOnAddressDuration: number | null
  taxCrOnlyFlag: boolean
  trialPeriod: boolean // Employment trial
  utmParams: string
}

const initialAddress = {
  city: '',
  houseNumber: '',
  landRegistryNumber: '',
  street: '',
  zip: '',
}

export const initialState: ImmutableGenericType<ClientState> = Immutable({
  amountFrom: 0,
  amountTo: 0,
  cacheSmsSendResponse: undefined,
  cacheVerifySmsStatus: undefined,
  citizenship: '52',
  consentCommUnsecured: false,
  consentInstantMessaging: false,
  consentToOfferMarketing: false,
  contactAddress: { ...initialAddress },
  countryOfBirth: '52',
  differentContactAddress: false,
  education: null,
  email: '',
  employedSince: undefined,
  employedTo: undefined,
  employerAddress: { ...initialAddress },
  employerId: '',
  employerName: '',
  employmentTypeCode: '',
  firstName: '',
  housingExpenditure: null,
  identityCardExpiration: undefined,
  identityCardNumber: '',
  lastName: '',
  livingType: null,
  maritalStatus: '',
  minedIdentityStatus: undefined,
  net3MonthHouseholdIncome: null,
  net3MonthIncome: null,
  noticePeriod: false,
  numberOfDependents: null,
  otherCostOfLiving: null,
  otherDocumentType: null,
  otherMonthlyInstallments: null,
  pep: undefined,
  pepReason: undefined,
  permanentAddress: { ...initialAddress },
  personalIdentificationNumber: '',
  phoneNumber: '',
  placeOfBirth: '',
  professionCode: '',
  stayOnAddressDuration: null,
  taxCrOnlyFlag: false,
  trialPeriod: false,
  utmParams: '',
})

// todo: move it to the mappers or reducer
const mapAddress = (address?: Address) =>
  address
    ? {
        city: address.city || initialAddress.city,
        houseNumber: address.houseNumber || initialAddress.houseNumber,
        landRegistryNumber: address.landRegistryNumber || initialAddress.landRegistryNumber,
        street: address.street || initialAddress.street,
        zip: address.zip || initialAddress.zip,
      }
    : initialAddress

export const fromCbl = (cblData: any) => ({
  consentCommUnsecured: get(
    cblData,
    'clientParameters.consentCommUnsecured',
    initialState.consentCommUnsecured
  ),

  consentInstantMessaging: get(
    cblData,
    'clientParameters.consentInstantMessaging',
    initialState.consentInstantMessaging
  ),

  consentToOfferMarketing: get(
    cblData,
    'clientParameters.consentToOfferMarketing',
    initialState.consentToOfferMarketing
  ),
  email: get(cblData, 'clientParameters.email', initialState.email),
  firstName: get(cblData, 'clientParameters.firstName', initialState.firstName),
  lastName: get(cblData, 'clientParameters.lastName', initialState.lastName),
  phoneNumber: get(cblData, 'clientParameters.mobilePhoneNumber.number', initialState.phoneNumber),

  permanentAddress: mapAddress(get(cblData, 'clientParameters.permanentAddress')),
  contactAddress: mapAddress(get(cblData, 'clientParameters.contactAddress')),
  differentContactAddress:
    !!get(cblData, 'clientParameters.contactAddress.street') &&
    (get(cblData, 'clientParameters.permanentAddress.street') !==
      get(cblData, 'clientParameters.contactAddress.street') ||
      get(cblData, 'clientParameters.permanentAddress.landRegistryNumber') !==
        get(cblData, 'clientParameters.contactAddress.landRegistryNumber') ||
      get(cblData, 'clientParameters.permanentAddress.houseNumber') !==
        get(cblData, 'clientParameters.contactAddress.houseNumber') ||
      get(cblData, 'clientParameters.permanentAddress.city') !==
        get(cblData, 'clientParameters.contactAddress.city') ||
      get(cblData, 'clientParameters.permanentAddress.zip') !==
        get(cblData, 'clientParameters.contactAddress.zip')),

  identityCardNumber: get(
    cblData,
    'clientParameters.identityCard.number',
    initialState.identityCardNumber
  ),
  identityCardExpiration: get(
    cblData,
    'clientParameters.identityCard.expiration',
    initialState.identityCardExpiration
  ),
  personalIdentificationNumber: get(
    cblData,
    'clientParameters.personalIdentificationNumber',
    initialState.personalIdentificationNumber
  ),
  maritalStatus: get(cblData, 'clientParameters.maritalStatus', initialState.maritalStatus),
  citizenship: get(cblData, 'clientParameters.citizenship', initialState.citizenship),
  placeOfBirth: get(cblData, 'clientParameters.placeOfBirth', initialState.placeOfBirth),
  countryOfBirth: get(cblData, 'clientParameters.countryOfBirth', initialState.countryOfBirth),

  employmentTypeCode: get(
    cblData,
    'clientParameters.employment.employmentTypeCode',
    initialState.employmentTypeCode
  ),
  professionCode: get(
    cblData,
    'clientParameters.employment.occupationCode',
    initialState.professionCode
  ),

  employerAddress: mapAddress(get(cblData, 'clientParameters.employment.employerAddress')),

  employerId: get(cblData, 'clientParameters.employment.employerId', initialState.employerId),
  employerName: get(cblData, 'clientParameters.employment.employerName', initialState.employerName),
  employedSince: get(
    cblData,
    'clientParameters.employment.employedSince',
    initialState.employedSince
  ),
  employedTo: get(cblData, 'clientParameters.employment.employedTo', initialState.employedTo),
  noticePeriod: get(cblData, 'clientParameters.employment.noticePeriod', initialState.noticePeriod),
  trialPeriod: get(cblData, 'clientParameters.employment.trialPeriod', initialState.trialPeriod),

  net3MonthIncome: get(cblData, 'clientParameters.net3MonthIncome', initialState.net3MonthIncome),
  net3MonthHouseholdIncome: get(
    cblData,
    'clientParameters.net3MonthHouseholdIncome',
    initialState.net3MonthHouseholdIncome
  ),
  taxCrOnlyFlag: get(cblData, 'clientParameters.taxCrOnlyFlag', initialState.taxCrOnlyFlag),

  otherMonthlyInstallments: get(
    cblData,
    'clientParameters.installmentsOutsideMoneta',
    initialState.otherMonthlyInstallments
  ),
  otherCostOfLiving: get(
    cblData,
    'clientParameters.otherCostOfLiving',
    initialState.otherCostOfLiving
  ),
  numberOfDependents: get(
    cblData,
    'clientParameters.numberOfDependents',
    initialState.numberOfDependents
  ),

  education: get(cblData, 'clientParameters.education', initialState.education),
  stayOnAddressDuration: get(cblData, 'clientParameters.permanentAddress.stayOnAddressDuration'),
  livingType: get(cblData, 'clientParameters.livingType', initialState.livingType),
  otherDocumentType: get(
    cblData,
    'clientParameters.otherDocumentType',
    initialState.otherDocumentType
  ),
  pep: mapPep(cblData),
  pepReason: get(cblData, 'clientParameters.pepReason'),
})

export const mapPep = (cblData: any) => {
  if (get(cblData, 'clientParameters.pepSet')) {
    if (get(cblData, 'clientParameters.pepReason') !== null) {
      return PepOptions.Yes
    }
    return PepOptions.No
  }

  return undefined
}
